import type { ShellStringKey } from "./strings";
import type { ShellTabKey } from "./tabs";

/**
 * Таблица адресов оболочки — единственный источник для нижней панели, для
 * заголовка верхней и для «←» (PLAN §2.4). Страница сама ничего этого не
 * решает: она в лучшем случае уточняет заголовок или кнопки через
 * useShellBar.
 *
 * Виды адресов:
 *  - tab — корень вкладки: нижняя панель видна, выбрана `tab` («none» — не
 *    выбрана ни одна: меню профиля и профиль, как в приложении);
 *  - sub — экран внутри вкладки: верхняя панель с «←», нижней нет;
 *  - layer — страница сама во весь экран со своими панелями (аналитика, лига,
 *    достижения): у оболочки здесь панелей нет;
 *  - redirect — сразу на другой адрес (`redirect`).
 *
 * «←» ведёт на `parent` (решение D14, правило владельца «назад — на уровень
 * вверх»). `fromState` — сначала адрес, с которого сюда пришли
 * (location.state.from), если он из кабинета. `back: "history"` — «←»
 * «Общения и встреч»: как popBackStack приложения, шаг назад, а на первой
 * странице вкладки — на «Тренировки» (D5).
 */
export type ShellRouteKind = "tab" | "sub" | "layer" | "redirect";

export type ShellAction =
    | "bell" // уведомления, со значком непрочитанных (Тренировки)
    | "person" // меню профиля
    | "addMeeting" // зелёный «+» → новая встреча (Общение и встречи)
    | "shield" // модерация (только сотрудникам)
    | "addGroup" // новая группа
    | "searchGroup" // найти группу
    | "refresh"
    | "personSearch" // найти людей (Друзья)
    | "tokens"; // остаток токенов (Умный поиск)

export interface ShellRoute {
    kind: ShellRouteKind;
    /** Какая вкладка горит; для sub — вкладка, которой экран принадлежит. */
    tab: ShellTabKey | "none";
    /** Заголовок; null — его даёт страница (useShellBar) или его нет. */
    title: ShellStringKey | null;
    /** Меню профиля: заголовок — имя человека, без имени — `title`. */
    titleFromName?: boolean;
    /** Куда «←» (без языка, с подставленными :параметрами); null — «←» нет. */
    parent: string | null;
    /** location.state.from (адрес кабинета) важнее `parent`. */
    fromState?: boolean;
    /** «←» вкладки «Общение и встречи»: шаг назад, если есть куда, иначе `parent` (D5). */
    back?: "history";
    actions: ShellAction[];
    /** Фон верхней панели: background #0A0A0A или surface #1F1F1F (Чаты, чат, Найти группу). */
    tone: "background" | "surface";
    /** Иконка «человек» на «Календаре» в приложении белая, а не #CAC4D0 (CalendarScreen.kt). */
    personWhite?: boolean;
    /** redirect: куда (без языка). */
    redirect?: string;
    /** Шаблон строки таблицы, по которому совпал адрес (для тестов и отладки). */
    pattern: string;
    /** Значения :параметров адреса. */
    params: Record<string, string>;
}

type Row = Omit<ShellRoute, "params" | "pattern" | "actions" | "tone"> & {
    actions?: ShellAction[];
    tone?: "background" | "surface";
};

const tab = (t: ShellTabKey | "none", title: ShellStringKey | null, extra: Partial<Row> = {}): Row => ({
    kind: "tab",
    tab: t,
    title,
    parent: null,
    ...extra,
});
const sub = (t: ShellTabKey | "none", title: ShellStringKey | null, parent: string, extra: Partial<Row> = {}): Row => ({
    kind: "sub",
    tab: t,
    title,
    parent,
    ...extra,
});
const layer = (): Row => ({ kind: "layer", tab: "analytics", title: null, parent: null });

const MENU = "/account/menu";
const TRAINING = "/account/training";
/** Разделы «Чаты» и «Люди» вкладки «Общение и встречи» (CommunityHomeApp). */
const COMMUNITY_CHATS = "/account/community?tab=chats";
const COMMUNITY_PEOPLE = "/account/community?tab=people";
/** «Диета и диагностика» и её раздел «Диагностика» (хаб админа, ?tab=). */
const DIET = "/account/diet";
const DIET_DIAGNOSTICS = "/account/diet?tab=diagnostics";

/**
 * Строки таблицы: шаблон адреса без языка → строка. Порядок не важен —
 * точный шаблон всегда важнее «хвоста» `/*`; общий запасной — в самом низу
 * resolveShellRoute. Шаблоны пишутся строчными буквами (адрес сравнивается
 * без учёта регистра, как его сравнивает роутер).
 */
const TABLE: Record<string, Row> = {
    "/account": { kind: "redirect", tab: "training", title: null, parent: null, redirect: "/account/training" },

    // Тренировки, Календарь — как в приложении v5 (MenuScreen.kt, CalendarScreen.kt).
    // Значков датчиков «Тренировок» (пульс, термометр, место) у сайта нет: это
    // устройства телефона, сайту их не видно. Самого календаря на сайте нет
    // (владелец, 03.10, 09.10 и 20.10) — вкладка «Календарь» (владелец, 20.10:
    // «вкладку как в приложении нужно») открывает вкладку приложения, а без
    // приложения — карточку «Календарь — в приложении» (AppOnlySectionPage).
    "/account/training": tab("training", "training", { actions: ["bell", "person"] }),
    "/account/calendar": tab("calendar", "calendar", { actions: ["person"], personWhite: true }),
    // «Зоны пульса и длина шага» — раздел вкладки (ряд «Тренировки | Зоны
    // пульса и шаг», tabSections.ts; владелец, 09.10): корень вкладки, как
    // сами «Тренировки». Его открывают ещё часы и приложение (окно сайта) —
    // в окне «←» первой страницы возвращает в приложение (PhoneShell).
    "/account/training-basis": tab("training", "training", { actions: ["bell", "person"] }),

    // Аналитика и баллы — слои во весь экран со своим «‹» (D3)
    "/account/analytics": layer(),
    "/account/league-users": layer(),
    "/account/league-users/*": layer(),
    "/account/achievements": layer(),
    "/account/achievements/*": layer(),
    // «История баллов» — вкладка «Прогресс и лига» (горит на панели), «‹» — на
    // «Баллы» слоя или туда, откуда пришли; неделю и её «‹» страница даёт сама
    // (useShellBar).
    "/account/points-history": tab("analytics", "pointsHistory", {
        parent: "/account/analytics?tab=points",
        fromState: true,
    }),

    // Диета и диагностика — вкладка приложения v5
    "/account/diet": tab("diet", "diet", { actions: ["person"] }),

    // Курсы
    "/account/my-courses-new": tab("courses", "courses", { actions: ["person"] }),
    "/account/my-courses-new/group/:groupId": sub("courses", null, "/account/my-courses-new"),
    "/account/my-courses-new/:courseId": sub("courses", null, "/account/my-courses-new"),
    "/account/my-courses-new/:courseId/:lessonId": sub("courses", null, "/account/my-courses-new/:courseId"),
    // «Мои лекции» и «Эфиры» (эфиры — пока админам) — разделы вкладки «Курсы»
    // (ряд «Курсы | Мои лекции | Эфиры», tabSections.ts): корни вкладки, как сами «Курсы».
    "/account/lectures": tab("courses", "courses", { actions: ["person"] }),
    "/account/streams": tab("courses", "courses", { actions: ["person"] }),

    // Умный поиск — вкладка приложения v5 (вместо «Ответов на вопросы»: у
    // приложения нажатие сразу открывает страницу поиска). Прежний адрес
    // «Ответов» ведёт туда же.
    "/account/answers": { kind: "redirect", tab: "search", title: null, parent: null, redirect: "/account/questions" },
    // Разделы вкладки — ряд «Поиск | Специалист | База знаний» (tabSections.ts):
    // каждый — корень вкладки, заголовок — имя вкладки.
    "/account/questions": tab("search", "smartSearch", { actions: ["tokens"] }),
    "/account/specialist-chat": tab("search", "smartSearch", { actions: ["person"] }),
    "/account/knowledge": tab("search", "smartSearch", { actions: ["person"] }),

    // Общение и встречи — вкладка, как «Тренировки» и «Календарь»: без «←» и
    // без зелёного «+» (в приложении v5 «Создать встречу» — в разделе
    // «Встречи»), справа — колокольчик и меню профиля (CommunityMapScreen.kt).
    // Разделы «Встречи | Чаты | Люди» — в адресе (?tab=), их меняют на месте.
    "/account/community": tab("community", "community", { actions: ["bell", "person"] }),
    // Чаты — раздел вкладки (?tab=chats); отдельный адрес списка остался для
    // старых ссылок. Чат, группы и модерация — «←» в раздел «Чаты»; «···»
    // раздела открывает группы, модерацию и поиск людей с state.from — «←»
    // туда, откуда пришли.
    "/account/community/chats": sub("community", "chats", COMMUNITY_CHATS, {
        tone: "surface",
        actions: ["shield", "addGroup", "searchGroup", "refresh"],
    }),
    "/account/community/group/:groupId": sub("community", "chat", COMMUNITY_CHATS, { tone: "surface" }),
    "/account/community/group/:groupId/info": sub("community", "groupInfo", "/account/community/group/:groupId"),
    "/account/community/groups": sub("community", "groupsFind", COMMUNITY_CHATS, { tone: "surface", fromState: true }),
    "/account/community/groups/new": sub("community", "groupCreate", COMMUNITY_CHATS, { fromState: true }),
    "/account/community/moderation": sub("community", "moderation", COMMUNITY_CHATS, { fromState: true }),
    "/account/community/map": sub("community", "meetingsMap", "/account/community"),
    "/account/community/meetings": sub("community", "meetingsAll", "/account/community"),
    // Люди — раздел вкладки (?tab=people): друзья, поиск людей и добрые дела — «←» туда.
    "/account/community/friends": sub("community", "friends", COMMUNITY_PEOPLE, {
        actions: ["personSearch"],
        fromState: true,
    }),
    "/account/community/people": sub("community", "people", COMMUNITY_PEOPLE, { fromState: true }),
    "/account/community/person/:personId": sub("community", "person", "/account/community/people", {
        fromState: true,
    }),
    "/account/community/create": sub("community", "createMeeting", "/account/community"),
    "/account/activity/:id": sub("community", "meeting", "/account/community", { fromState: true }),
    "/account/community/club": sub("community", "club", "/account/profile", { fromState: true }),
    "/account/good-deeds": sub("community", "goodDeeds", COMMUNITY_PEOPLE, { fromState: true }),

    // Меню профиля (новое) и его экраны
    // Меню профиля: «← имя» и колокольчик справа, как ProfileMenuScreen приложения.
    "/account/menu": tab("none", "profile", {
        titleFromName: true,
        parent: "/account/training",
        fromState: true,
        actions: ["bell"],
    }),
    "/account/profile": tab("none", "profile", { parent: MENU }),
    // Профиль v2: своя карточка (заголовок «Мой профиль» и ⚙ даёт страница) и «Как меня видят».
    "/account/me": tab("none", "profile", { parent: MENU }),
    "/account/me/preview": sub("none", "profile", "/account/me"),
    "/account/settings": sub("none", "settings", MENU),
    "/account/notifications": sub("none", "notifications", "/account/training", { fromState: true }),
    // Части «Диеты и диагностики». У админа это экраны вглубь хаба: итоги
    // ЗОЖ («Диагностика») открывают диагностику, деревья и сферу жизни, «⋯»
    // хаба — консультации; у остальных — раздел и «···» ряда вкладки
    // (tabSections.ts). «←» — туда, откуда пришли; без state — в хаб.
    "/account/diagnose": sub("diet", "diagnose", DIET_DIAGNOSTICS, { fromState: true }),
    "/account/consultations": sub("diet", "consultations", DIET, { fromState: true }),
    "/account/consultations-email": sub("diet", "consultationsEmail", DIET, { fromState: true }),
    "/account/trees": sub("diet", "trees", DIET_DIAGNOSTICS, { fromState: true }),
    "/account/area_of_life": sub("diet", "areaOfLife", DIET_DIAGNOSTICS, { fromState: true }),
    // «Реферальную программу» открывают меню профиля и «Пригласить друга»
    // раздела «Люди» — «←» туда, откуда пришли; без state — в меню профиля.
    "/account/affiliate": sub("none", "affiliate", MENU, { fromState: true }),
    // «Мои отзывы» — плитка меню профиля (и «⋯» хаба диеты админа): «←» туда, откуда пришли.
    "/account/reviews": sub("none", "reviews", MENU, { fromState: true }),
    "/account/products": sub("none", "products", MENU),
    "/account/products/*": sub("none", "products", MENU),
    "/account/gifts": sub("none", "gifts", MENU),
    "/account/gifts/*": sub("none", "gifts", MENU),

    // Бывшие страницы тренировок: в приложении это «Календарь». Календарь,
    // план и загрузка файла скрыты рубильником SITE_WORKOUT_PAGES_HIDDEN (их
    // адреса открывают «Тренировки»), строки — для отката и окна приложения.
    // Тестовую тренировку открывают «Тренировки» — «←» туда, откуда пришли.
    "/account/workouts": sub("none", "workouts", TRAINING),
    "/account/testing-workout": sub("training", "testingWorkout", TRAINING, { fromState: true }),
    "/account/plan-create": sub("none", "planCreate", TRAINING),
    "/account/fit-upload": sub("none", "fitUpload", TRAINING),
};

/** Все шаблоны таблицы (для тестов). */
export const SHELL_ROUTE_PATTERNS: readonly string[] = Object.keys(TABLE);

/** Любой другой адрес кабинета: экран без своей строки — «←» в меню профиля. */
const FALLBACK: Row = sub("none", null, MENU);

/** Похоже ли на язык сайта первое звено адреса («ru», «en», «cn»…). */
const LANG_SEGMENT = /^[a-z]{2}$/i;

/**
 * Адрес без языка, без «/» в конце: "/ru/account/menu/" → "/account/menu".
 * Адрес вне кабинета — null.
 */
export const stripLang = (pathname: string): string | null => {
    const clean = (pathname || "").split(/[?#]/)[0].replace(/\/+$/, "") || "/";
    const segs = clean.split("/").filter(Boolean);
    if (segs.length >= 2 && LANG_SEGMENT.test(segs[0]) && segs[1].toLowerCase() === "account") {
        segs.shift();
    }
    if (segs[0]?.toLowerCase() !== "account") return null;
    return "/" + segs.join("/");
};

const match = (pattern: string, path: string): Record<string, string> | null => {
    const p = pattern.split("/").filter(Boolean);
    const s = path.split("/").filter(Boolean);
    const splat = p[p.length - 1] === "*";
    if (splat ? s.length < p.length : s.length !== p.length) return null;
    const params: Record<string, string> = {};
    for (let i = 0; i < p.length; i++) {
        if (p[i] === "*") {
            params["*"] = s.slice(i).join("/");
            return params;
        }
        if (p[i].startsWith(":")) {
            params[p[i].slice(1)] = decodeURIComponentSafe(s[i]);
        } else if (p[i] !== s[i].toLowerCase()) {
            return null;
        }
    }
    return params;
};

const decodeURIComponentSafe = (v: string): string => {
    try {
        return decodeURIComponent(v);
    } catch {
        return v;
    }
};

/** «:param» шаблона → значение адреса. */
const fill = (template: string, params: Record<string, string>): string =>
    template.replace(/:([A-Za-z]+)/g, (_, k: string) => encodeURIComponent(params[k] ?? ""));

/** Чем меньше «:param» и «*», тем точнее шаблон. */
const specificity = (pattern: string): number =>
    pattern.split("/").reduce((n, seg) => n + (seg === "*" ? 100 : seg.startsWith(":") ? 1 : 0), 0);

const PATTERNS_BY_SPECIFICITY = [...SHELL_ROUTE_PATTERNS].sort((a, b) => specificity(a) - specificity(b));

/**
 * Строка таблицы для адреса — с языком или без, с «/» в конце или без.
 * Адрес вне кабинета — null; адрес кабинета без своей строки — общий
 * запасной (sub, «←» в меню профиля, заголовок от страницы).
 */
export const resolveShellRoute = (pathname: string): ShellRoute | null => {
    const path = stripLang(pathname);
    if (path === null) return null;
    for (const pattern of PATTERNS_BY_SPECIFICITY) {
        const params = match(pattern, path);
        if (!params) continue;
        const row = TABLE[pattern];
        return {
            ...row,
            actions: row.actions ?? [],
            tone: row.tone ?? "background",
            parent: row.parent === null ? null : fill(row.parent, params),
            redirect: row.redirect,
            pattern,
            params,
        };
    }
    return { ...FALLBACK, actions: [], tone: "background", pattern: "*", params: {} };
};

/**
 * Адрес кабинета из location.state.from (только «/…/account…», не чужой сайт).
 * Его же читает слой «Аналитика и баллы»: вкладка оболочки, открывшая слой с
 * заменой записи, оставляет здесь, какую вкладку он заменил.
 */
export const shellStateFrom = (state: unknown): string | null => {
    const from = (state as { from?: unknown } | null)?.from;
    const s = typeof from === "string" ? from : (from as { pathname?: unknown } | null)?.pathname;
    if (typeof s !== "string" || !s.startsWith("/") || s.startsWith("//")) return null;
    return stripLang(s) === null ? null : s;
};

/** Адрес с языком: "/account/menu" → "/ru/account/menu"; уже с языком — как есть. */
export const withLang = (lang: string, path: string): string => {
    const segs = path.split("/").filter(Boolean);
    if (segs.length >= 2 && LANG_SEGMENT.test(segs[0]) && segs[1].toLowerCase() === "account") return path;
    return `/${lang || "ru"}${path.startsWith("/") ? path : "/" + path}`;
};

/**
 * Адрес кабинета из `?from=` адреса страницы — то же, что state.from, но
 * переживает перезагрузку и приходит по ссылке. Только «/…/account…».
 */
export const shellSearchFrom = (search?: string | null): string | null => {
    if (!search) return null;
    try {
        return shellStateFrom({ from: new URLSearchParams(search).get("from") ?? undefined });
    } catch {
        return null;
    }
};

/** Куда ведёт «←» этого адреса (с языком); null — «←» нет. */
export const shellParentHref = (
    route: ShellRoute,
    lang: string,
    state?: unknown,
    search?: string | null,
): string | null => {
    if (route.fromState) {
        const from = shellStateFrom(state) ?? shellSearchFrom(search);
        if (from) return withLang(lang, from);
    }
    return route.parent === null ? null : withLang(lang, route.parent);
};

/** Корень вкладки (или её раздел): выше него в окне приложения — само приложение. */
export const isTabRoot = (route: ShellRoute): boolean => route.kind === "tab" && route.parent === null;

/**
 * Что сделать по «←» (решение D14, правило владельца «назад — на уровень вверх»):
 *  - окно приложения: корень вкладки — закрыть окно (выше вкладки — само
 *    приложение), сколько бы шагов ни было пройдено внутри окна; первая
 *    страница окна — тоже закрыть (владелец, 09.10: «← Умный поиск» не
 *    выводил из окна, а ходил по всем пройденным страницам);
 *  - «Общение и встречи»: есть куда назад (history.state.idx > 0) — шаг назад,
 *    иначе на `parent` (D5, как popBackStack приложения);
 *  - иначе, если предыдущая запись истории и есть родитель (или `from`) — шаг
 *    назад, чтобы история не росла; иначе — на родителя С ЗАМЕНОЙ записи:
 *    переход «вверх» не копит историю, и два экрана не могут гонять друг
 *    друга по кругу (владелец, 09.10: «Мои лекции» ↔ «Курсы» без конца).
 * `previousPath` — адрес предыдущей записи, если оболочка его знает.
 */
export type ShellBackAction =
    | { type: "pop" }
    | { type: "replace"; to: string }
    | { type: "close" }
    | { type: "none" };

export const decideShellBack = (args: {
    route: ShellRoute;
    lang: string;
    state?: unknown;
    /** location.search — для `?from=` у строк с fromState. */
    search?: string | null;
    idx?: number | null;
    previousPath?: string | null;
    /** Страница открыта в окне приложения (?source=app). */
    appWindow?: boolean;
}): ShellBackAction => {
    const { route, lang, state, search, idx, previousPath, appWindow } = args;
    const canPop = typeof idx === "number" && idx > 0;
    if (appWindow && (!canPop || isTabRoot(route))) return { type: "close" };
    if (route.back === "history" && canPop) return { type: "pop" };
    const to = shellParentHref(route, lang, state, search);
    if (!to) return { type: "none" };
    if (canPop && previousPath && samePath(previousPath, to)) return { type: "pop" };
    return { type: "replace", to };
};

const samePath = (a: string, b: string): boolean => {
    const norm = (p: string) => {
        const path = p.split(/[?#]/)[0].replace(/\/+$/, "").toLowerCase();
        return stripLang(path) ?? path;
    };
    return norm(a) === norm(b);
};

/** Цепочка «←» от адреса до корня вкладки (для тестов): адреса без языка. */
export const parentChain = (pathname: string, maxSteps = 8): string[] => {
    const chain: string[] = [];
    let route = resolveShellRoute(pathname);
    while (route && route.kind !== "tab" && route.parent && chain.length < maxSteps) {
        chain.push(route.parent);
        route = resolveShellRoute(route.parent);
    }
    return chain;
};
